import { observer } from 'mobx-react';
import React, { useCallback, useEffect } from 'react';
import ItemsListRow from '../../components/ItemListRow/ItemListRow';
import itemsStore from '../../store/itemsStore';
import { Icons } from 'src/assets/fonts/Icons/icons';
import { SORTING_DIRECTION } from 'src/constants/main';
import { localeStore } from 'src/mobx/localesStore';
import { PRODUCTS_SORT_COLUMN } from 'src/types/product';
import { formatImagePath } from 'src/utils/formatter';
import { emptyArray } from 'src/utils/helpers';
import ColumnTitle from 'src/view/components/ColumnTitle/ColumnTitle';
import Pagination from 'src/view/components/Controls/PaginationOld/PaginationOld';
import ScrollableBlock from 'src/view/containers/ScrollableBlock/ScrollableBlock';

const ItemsList: React.FC = observer((): JSX.Element => {
  useEffect(() => {
    itemsStore.fetchItems(1);
    itemsStore.fetchItemsCount();
  }, []);

  const setFilters = useCallback((sortField: PRODUCTS_SORT_COLUMN | undefined, sortDir: SORTING_DIRECTION | undefined) => {
    itemsStore.setFilters({ sort_field: sortField, sort_dir: sortDir });
  }, []);

  const checkDeletedItem: boolean = itemsStore.pagination.data.length && itemsStore.pagination.data[0].is_delete ? true : false;
  const noFoundText: string = localeStore.t(
    itemsStore.filters.search || itemsStore.filters.availability || itemsStore.filters.category || itemsStore.foundItemsInSystem
      ? 'no_items_found'
      : 'no_items_yet'
  );

  return (
    <div className="d-flex flex-column flex-grow-1 pt-4">
      {!itemsStore.pagination.total && !itemsStore.isLoading ? (
        <div className="d-flex flex-column justify-content-center align-items-center mt-2">
          <img src={formatImagePath(`${Icons.Items}.svg`)} alt={noFoundText} className="image__no-found" />
          <p className="family-medium text-size-24 color-gray2">{noFoundText}</p>
        </div>
      ) : (
        <>
          <div className="d-flex flex-row mb-4 pr-5">
            <ColumnTitle<PRODUCTS_SORT_COLUMN>
              title="Item"
              columnName={PRODUCTS_SORT_COLUMN.NAME}
              currentField={itemsStore.filters.sort_field}
              currentDir={itemsStore.filters.sort_dir}
              setFilters={setFilters}
              className="w-25"
            />
            {checkDeletedItem ? null : (
              <>
                <ColumnTitle<PRODUCTS_SORT_COLUMN>
                  title="txt_category"
                  columnName={PRODUCTS_SORT_COLUMN.CATEGORY}
                  currentField={itemsStore.filters.sort_field}
                  currentDir={itemsStore.filters.sort_dir}
                  setFilters={setFilters}
                  className="w-15"
                />
                <ColumnTitle title="order_type" className="w-15" />
                <ColumnTitle<PRODUCTS_SORT_COLUMN>
                  title="txt_price"
                  tooltipMessage="first_size_price_shown"
                  columnName={PRODUCTS_SORT_COLUMN.PRICE}
                  currentField={itemsStore.filters.sort_field}
                  currentDir={itemsStore.filters.sort_dir}
                  setFilters={setFilters}
                  className="w-15"
                />
                <ColumnTitle<PRODUCTS_SORT_COLUMN>
                  title="sort"
                  columnName={PRODUCTS_SORT_COLUMN.SORT}
                  currentField={itemsStore.filters.sort_field}
                  currentDir={itemsStore.filters.sort_dir}
                  setFilters={setFilters}
                  className="w-10"
                />
                <ColumnTitle title="in_stock" className="w-10" />
                <ColumnTitle title="availability" className="w-10" />
              </>
            )}
          </div>
          <ScrollableBlock showScrollbar scrollClassName="d-flex flex-column full-height pr-4" className="mb-3">
            {itemsStore.isLoading && !itemsStore.pagination.total
              ? emptyArray().map((_, index) => <ItemsListRow key={'key-' + String(index)} index={index} />)
              : itemsStore.pagination.data.map((item, index) => <ItemsListRow key={item.id} index={index} item={item} />)}
          </ScrollableBlock>
        </>
      )}
      <Pagination pagination={itemsStore.pagination} onChange={itemsStore.fetchItems} />
    </div>
  );
});

export default ItemsList;
